import { Face } from '@/features/game/Dice'

/**
 * «Как играть» — для того, кто садится за стол впервые.
 *
 * Показывается модальным окном поверх экрана (Modal), а не вместо него: за
 * правилами приходят и посреди партии («а прыгать-то можно?»), и уходить за
 * этим с доски значило бы уйти с хода. Адрес при этом свой, /rules, — им
 * делятся, и «назад» браузера окно закрывает.
 *
 * Не пересказ docs/RULES.md: там источник правды со спорными случаями и
 * обоснованиями, и читать его перед первой партией незачем. Здесь только то,
 * без чего не сходить, — и в том порядке, в каком новичок на это натыкается:
 * сначала как ходить, потом чем это ограничено, и в конце как сесть за стол.
 *
 * Правила разложены парами «что — почему»: короткое начало строки читается
 * само по себе, поэтому страницу можно просмотреть по левому краю, не читая
 * целиком. За этим же и деление на карточки: к нужной возвращаются, а не
 * перечитывают всё.
 */

type Rule = {
  /** Начало строки: по нему правило и находят взглядом. */
  lead: string
  text: string
}

type Section = {
  title: string
  rules: Rule[]
}

const SECTIONS: Section[] = [
  {
    title: 'Ход',
    rules: [
      {
        lead: 'Два кубика.',
        text: 'Бросаете их сами, кнопкой над доской: бросок — это действие, а не то, что случается за вас.',
      },
      {
        lead: 'Одной фишкой или двумя.',
        text: 'Выпавшим можно сходить одной фишкой на сумму, а можно порознь — двумя разными или одной подряд.',
      },
      {
        lead: 'Против часовой стрелки.',
        text: 'Фишки идут только вперёд, по кругу, и заходят в дом со своего луча.',
      },
      {
        lead: 'Ходить обязательно.',
        text: 'Если играются оба кубика — играете оба. Если только один — больший. Если ничем сходить нельзя, ход уходит дальше, а кубики сгорают.',
      },
    ],
  },
  {
    title: 'Двор и рубка',
    rules: [
      {
        lead: 'Из двора — только по шестёрке.',
        text: 'Другого выхода нет. Вышедшая фишка встаёт на свою стартовую клетку.',
      },
      {
        lead: 'Чужая фишка на вашей клетке рубится.',
        text: 'Встали на её клетку — она уходит во двор и выходит оттуда заново, снова по шестёрке. Безопасных клеток на поле нет.',
      },
      {
        lead: 'Через фишки не прыгают.',
        text: 'Любая фишка на пути — своя или чужая — ход останавливает. На одной клетке двух фишек не бывает.',
      },
      {
        lead: 'Срубленная возвращается сразу.',
        text: 'Если шестёрка есть в этом же броске, во двор она уйдёт и выйдет тем же ходом.',
      },
    ],
  },
  {
    title: 'Дополнительные броски',
    rules: [
      {
        lead: 'За каждую шестёрку — кубик.',
        text: 'Выпали две шестёрки — бросаете оба кубика ещё раз.',
      },
      {
        lead: 'За фишку в углу — кубик.',
        text: 'Считается фишка, которая пришла в угол этим ходом и осталась там, когда кубики кончились. Ночевавшая в углу с прошлого хода не считается — она никуда не приходила.',
      },
      {
        lead: 'В угол шестёркой — доброс один.',
        text: 'Шестёрка своё уже дала. А если в угол привёл другой кубик или сумма, добросов два: за шестёрку и за угол.',
      },
      {
        lead: 'Прыгать в угол выгодно последним кубиком.',
        text: 'Кубик, которым фишка уходит из угла, доброс отменяет — даже если сыграть его обязывает правило максимума.',
      },
      {
        lead: 'Больше двух за раз не бросают.',
        text: 'Кубиков за столом всего два, и лишний заработанный доброс сгорает вместе с броском.',
      },
    ],
  },
  {
    title: 'Углы',
    rules: [
      {
        lead: 'Четыре угловые клетки.',
        text: 'Это углы центрального квадрата, они же клетки круга. Фишка в углу может прыгнуть сразу в другой угол.',
      },
      {
        lead: 'Выпавшее — расстояние в углах.',
        text: '1 — следующий угол, 2 — через один, 3 — через два. Только вперёд по ходу.',
      },
      {
        lead: 'Промежуточные углы должны быть свободны.',
        text: 'В целевом мешает только своя фишка — чужую прыжок рубит.',
      },
      {
        lead: 'Прыжок не обязателен.',
        text: 'Правило максимума на него не распространяется: можно сходить и обычным ходом.',
      },
    ],
  },
  {
    title: 'Дом и победа',
    rules: [
      {
        lead: 'Вход в дом — точное число.',
        text: 'Перелёт недопустим, недоход возможен: фишка встанет ближе и войдёт следующим ходом.',
      },
      {
        lead: 'Побеждает занявший весь дом.',
        text: 'Клеток в доме ровно столько, сколько у вас фишек, — пять.',
      },
      {
        lead: 'Первым ходит выбросивший больше.',
        text: 'Считается наибольшее значение одним кубиком. При ничьей перебрасывают только те, у кого оно наибольшее.',
      },
    ],
  },
  {
    title: 'Как сесть за стол',
    rules: [
      {
        lead: 'Быстрая игра.',
        text: 'Подсаживает к тому, кто уже ищет соперника, а если таких нет — открывает ваш стол и ждёт.',
      },
      {
        lead: 'Открытый стол или стол по ссылке.',
        text: 'Открытый найдёт любой, за стол по ссылке сядут только те, кому вы её отправите. Ссылка — это и есть адрес партии.',
      },
      {
        lead: 'Компьютер.',
        text: 'Три уровня, садится сразу и готов сразу. Играет и с закрытой вкладкой: он на сервере, а не в браузере.',
      },
      {
        lead: 'Партию начинает готовность.',
        text: 'Не хозяин стола: каждый севший отмечается «готов», и партия стартует сама, когда готовы все и их не меньше двоих.',
      },
    ],
  },
  {
    /* Последним: это второй набор правил, и новичку за обычным столом он
       не нужен. Пары «что — почему» — как везде. */
    title: 'Сокровища — стол с бонусами',
    rules: [
      {
        lead: 'Выбирается при создании стола.',
        text: 'Переключателем «классика / сокровища». Классика остаётся как есть. Быстрая игра сажает за любой стол — какой он, видно до «готов».',
      },
      {
        lead: 'Бонусы появляются по ходу партии.',
        text: 'От двух до четырёх, в случайных местах кольца. Через три круга не взятые сгорают, и на поле тут же появляется новый набор. Догорающий бонус мигает и тлеет красным кольцом — это его последний круг.',
      },
      {
        lead: 'Забирает фишка, закончившая на клетке шаг.',
        text: 'Пройти мимо — не считается. Ход парой — это два шага, и остановка между ними тоже забирает.',
      },
      {
        lead: 'Сундук — сюрприз.',
        text: 'Внутри монеты, ещё один бросок или щит. Монеты бывают, только если вы вошли и партия идёт в задания — с людьми или с компьютером; иначе внутри всегда бросок или щит.',
      },
      {
        lead: 'Кубик — ещё один бросок.',
        text: 'Как за угол: бросаете, когда кончились кубики.',
      },
      {
        lead: 'Щит — фишку не срубить пять ваших ходов.',
        text: 'На её клетку нельзя встать ни ходом, ни прыжком, ни выходом со двора. Цифра на значке — сколько ходов осталось, на последнем значок мигает.',
      },
    ],
  },
]

export function RulesScreen() {
  return (
    <div className="rules">
      <div className="hero">
        {/* Те же кубики, что на лобби: страница узнаётся как часть игры. */}
        <div className="hero__dice" aria-hidden="true">
          <span className="die">
            <Face value={6} />
          </span>
          <span className="die">
            <Face value={3} />
          </span>
        </div>
        <h2 className="hero__title">Как играть</h2>
        <p className="hero__lead">
          У каждого пять фишек. Обойти круг, завести все пять домой — и партия ваша. Ниже всё, что
          для этого нужно знать.
        </p>
      </div>

      <div className="card rules__short">
        <h2 className="card__title">Если читать некогда</h2>
        <ol className="rules__steps">
          <li>Бросьте кубики и сходите: на сумму одной фишкой или порознь двумя.</li>
          <li>Из двора выходят только по шестёрке — с неё и начинается партия.</li>
          <li>Шестёрка и угол дают бросить ещё раз, поэтому ход бывает длинным.</li>
          <li>Встали на чужую фишку — сбили её во двор. И вас так же.</li>
        </ol>
      </div>

      {SECTIONS.map((section) => (
        <div className="card" key={section.title}>
          <h2 className="card__title">{section.title}</h2>
          <ul className="rules__list">
            {section.rules.map((rule) => (
              <li key={rule.lead}>
                <b>{rule.lead}</b> {rule.text}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
